/* eslint-disable no-bitwise */
import Compressor from 'compressorjs';

export class ImageAndColorUtilities {
    public static async cropImage(
        dataUrl: string,
        startX: number,
        startY: number,
        width: number,
        height: number,
    ): Promise<string> {
        return new Promise(resolve => {
            const img = new Image();
            img.onload = (): void => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = width;
                canvas.height = height;

                ctx?.drawImage(img, startX, startY, width, height, 0, 0, width, height);

                const croppedImage = canvas.toDataURL('base64');
                canvas?.remove();
                img?.remove();

                resolve(croppedImage);
            };
            img.src = dataUrl;
        });
    }

    public static async scaleImage(
        dataUrl: string,
        newWidth: number,
        newHeight: number,
        mode: 'scaleUp' | 'scaleDown' | 'both' = 'both',
        proportional = true,
    ): Promise<string> {
        return new Promise(resolve => {
            const img = new Image();
            img.onload = (): void => {
                if (
                    (img.width === newWidth && img.height === newHeight) ||
                    (mode === 'scaleDown' && img.width <= newWidth && img.height <= newHeight) ||
                    (mode === 'scaleUp' && img.width >= newWidth && img.height >= newHeight)
                ) {
                    img?.remove();
                    resolve(dataUrl);
                    return;
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                if (proportional) {
                    if (img.width > img.height) {
                        canvas.width = Math.floor(newWidth);
                        canvas.height = Math.floor((Math.floor(newWidth) * img.height) / img.width);
                    } else {
                        canvas.width = Math.floor((Math.floor(newHeight) * img.width) / img.height);
                        canvas.height = Math.floor(newHeight);
                    }
                } else {
                    canvas.width = Math.floor(newWidth);
                    canvas.height = Math.floor(newHeight);
                }

                ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

                const scaledImage = canvas.toDataURL('base64');
                canvas?.remove();
                img?.remove();

                resolve(scaledImage);
            };
            img.src = dataUrl;
        });
    }

    public static async compressImage(inputFile: File, minCompressionSize = 500000, quality = 0.8): Promise<File> {
        return new Promise<File>((resolve, reject) => {
            // eslint-disable-next-line sonarjs/constructor-for-side-effects
            new Compressor(inputFile, {
                quality,
                convertSize: minCompressionSize,
                success(file: File): void {
                    resolve(file);
                },
                error(error): void {
                    reject(error);
                },
            });
        });
    }

    public static parseRgbaStr(value: string): [r: number, g: number, b: number, a?: number | string] | null {
        // prepare value, cleanup whitespace
        value = value.replace(/\s/g, '');

        const rgbaArr = /^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?\)/i.exec(value);
        if (!rgbaArr) {
            return null;
        }

        const r = Number(rgbaArr[1]);
        const g = Number(rgbaArr[2]);
        const b = Number(rgbaArr[3]);

        const alpha = rgbaArr[4];
        if (alpha) {
            if (alpha.includes('%')) {
                return [r, g, b, alpha.trim()];
            } else {
                const alphaNum = Number(alpha);
                return [r, g, b, alphaNum];
            }
        }

        return [r, g, b];
    }

    public static rgba2hex(red: number, green: number, blue: number, alpha: number | string | null = null): string {
        const rgbStr =
            (red | (1 << 8)).toString(16).slice(1) +
            (green | (1 << 8)).toString(16).slice(1) +
            (blue | (1 << 8)).toString(16).slice(1);

        if (alpha != null) {
            let alphaNum: number;
            if (alpha.toString().includes('%')) {
                alphaNum = 1 * (Number(alpha) / 100);
            } else {
                alphaNum = Number(alpha);
            }

            if (alphaNum && !Number.isNaN(alphaNum)) {
                // multiply before convert to HEX
                const alphaStr = ((Number(alphaNum) * 255) | (1 << 8)).toString(16).slice(1);
                return `#${rgbStr}${alphaStr}`;
            }
        }
        return `#${rgbStr}`;
    }
}
